import { Check, ChevronDown, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/cn.js';

export interface MultiSelectOption {
  readonly value: string;
  readonly label: string;
  /** Segunda linha: razão social, cargo, o que desambigua homônimos. */
  readonly detail?: string | undefined;
  /**
   * Termos extras que a busca também alcança, sem aparecer na lista.
   *
   * É o que permite achar uma empresa digitando o CNPJ, que ninguém decora mas
   * todo mundo tem à mão no contrato.
   */
  readonly terms?: readonly string[] | undefined;
}

/** Normaliza para a busca ignorar acento, caixa e pontuação de CNPJ. */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[.\-/]/g, '');
}

function matches(option: MultiSelectOption, term: string): boolean {
  if (term === '') return true;

  const needle = normalize(term);
  const haystack = [option.label, option.detail ?? '', ...(option.terms ?? [])];

  return haystack.some((piece) => normalize(piece).includes(needle));
}

const ALTURA_MAXIMA = 320;

/**
 * Onde o painel é pendurado no DOM.
 *
 * Dentro de um `<dialog>`, o painel precisa ser filho do próprio diálogo: um
 * portal para o `body` cairia **atrás** do modal, porque o diálogo modal vive na
 * top layer e o resto da página não. Fora dele, o `body` serve.
 */
function destinoDoPortal(gatilho: HTMLElement | null): HTMLElement {
  return gatilho?.closest('dialog') ?? document.body;
}

/**
 * Painel suspenso compartilhado.
 *
 * **Sai do fluxo por portal, com posição fixa.** Como `absolute` dentro do
 * gatilho, ele era recortado por qualquer ancestral com `overflow` — foi o que
 * aconteceu dentro do modal "Incluir na equipe", onde o painel ficava engolido
 * pelo corpo rolável em vez de se sobrepor.
 *
 * **Superfície clara, com filete.** Era escuro, como todo sobreposto do sistema.
 * Sobre um formulário claro ele lia como outro assunto; claro com um filete de
 * `border` ele lê como o mesmo campo, aberto. É a exceção à regra de separar só
 * por tom — um elemento que flutua sobre o conteúdo precisa da própria borda
 * para dizer onde termina.
 */
function Panel({
  id,
  label,
  searchable,
  term,
  onTerm,
  anchorRef,
  panelRef,
  children,
}: {
  id: string;
  label: string;
  searchable: boolean;
  term: string;
  onTerm: (value: string) => void;
  anchorRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  const [montagem, setMontagem] = useState<{
    destino: HTMLElement;
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  /*
   * Mede antes de pintar, e remede a cada rolagem ou redimensionamento — com
   * `capture`, para pegar também a rolagem de um contêiner interno, que não
   * borbulha até a janela.
   */
  useLayoutEffect(() => {
    const medir = (): void => {
      const gatilho = anchorRef.current;
      if (!gatilho) return;

      const caixa = gatilho.getBoundingClientRect();
      const abaixo = window.innerHeight - caixa.bottom - 16;
      const acima = caixa.top - 16;
      const paraCima = abaixo < 200 && acima > abaixo;
      const altura = Math.min(ALTURA_MAXIMA, paraCima ? acima : abaixo);

      setMontagem({
        destino: destinoDoPortal(gatilho),
        top: paraCima ? caixa.top - altura - 8 : caixa.bottom + 8,
        left: caixa.left,
        width: caixa.width,
        maxHeight: altura,
      });
    };

    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [anchorRef]);

  // Sem medida ainda: o portal só existe depois do primeiro `useLayoutEffect`,
  // o que também evita ler a ref durante a renderização.
  if (!montagem) return null;

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role="listbox"
      aria-label={label}
      style={{
        position: 'fixed',
        top: montagem.top,
        left: montagem.left,
        minWidth: montagem.width,
        maxHeight: montagem.maxHeight,
      }}
      className="z-50 w-72 overflow-y-auto rounded-lg border border-border bg-card p-1 text-foreground shadow-none"
    >
      {searchable ? (
        <input
          autoFocus
          value={term}
          onChange={(event) => onTerm(event.target.value)}
          placeholder="Buscar…"
          aria-label={`Buscar em ${label.toLowerCase()}`}
          className="mb-1 h-9 w-full rounded-sm bg-muted px-2.5 text-[13px] text-foreground placeholder:text-muted-foreground"
        />
      ) : null}
      {children}
    </div>,
    montagem.destino,
  );
}

/**
 * Fecha ao clicar fora ou apertar Esc.
 *
 * "Fora" passou a ser fora do gatilho **e** fora do painel: com o painel num
 * portal, ele deixou de ser descendente do contêiner, e clicar numa opção
 * fechava o menu antes de a escolha ser registrada.
 */
function useDismiss(
  open: boolean,
  close: () => void,
): {
  containerRef: RefObject<HTMLDivElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent) => {
      const alvo = event.target as Node;
      if (containerRef.current?.contains(alvo)) return;
      if (panelRef.current?.contains(alvo)) return;
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  return { containerRef, panelRef };
}

/** Uma opção da lista, na superfície clara do painel. */
function Opcao({
  option,
  active,
  onSelect,
  marca,
}: {
  option: MultiSelectOption;
  active: boolean;
  onSelect: () => void;
  marca: 'caixa' | 'risco';
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onSelect}
      className="flex w-full items-start gap-2.5 rounded-sm px-2.5 py-2 text-left transition-colors hover:bg-muted"
    >
      {marca === 'caixa' ? (
        <span
          className={cn(
            'mt-px grid size-[16px] shrink-0 place-items-center rounded-2xs',
            active ? 'bg-primary' : 'bg-accent',
          )}
        >
          {active ? (
            <Check size={11} strokeWidth={3} className="text-primary-foreground" aria-hidden />
          ) : null}
        </span>
      ) : (
        <span className="mt-px grid size-[16px] shrink-0 place-items-center">
          {active ? <Check size={13} strokeWidth={3} className="text-primary" aria-hidden /> : null}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px]">{option.label}</span>
        {option.detail ? (
          <span className="block truncate text-[11px] text-muted-foreground">{option.detail}</span>
        ) : null}
      </span>
    </button>
  );
}

function NadaEncontrado() {
  return <p className="px-2.5 py-3 text-[13px] text-muted-foreground">Nada encontrado.</p>;
}

/**
 * Seleção múltipla em menu suspenso.
 *
 * Nada de `<select multiple>`: ele exige Ctrl+clique para escolher mais de um
 * item, o que quase ninguém descobre.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  variant = 'filter',
}: {
  label: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
  /** Liga o campo de busca — vale a partir de umas dez opções. */
  searchable?: boolean;
  /**
   * `filter` é o chip da barra de filtros: escurece quando há seleção e ganha
   * um "limpar" ao lado. `field` é campo de formulário — largura inteira, tom
   * de campo e **sem** o limpar, que ali apagaria escolhas de verdade em vez
   * de desfazer um recorte.
   */
  variant?: 'filter' | 'field';
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const panelId = useId();
  const gatilhoRef = useRef<HTMLButtonElement>(null);

  /*
   * Fechar limpa a busca. Ficava num efeito olhando `open`, o que dava o mesmo
   * resultado um quadro depois — tempo suficiente para o painel reabrir com o
   * termo antigo ainda na tela.
   */
  const fechar = useCallback(() => {
    setOpen(false);
    setTerm('');
  }, []);

  const { containerRef, panelRef } = useDismiss(open, fechar);

  function toggle(value: string): void {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  }

  const visible = options.filter((option) => matches(option, term));
  const count = selected.length;
  const isField = variant === 'field';

  return (
    <div ref={containerRef} className={cn('relative', isField && 'w-full')}>
      <button
        ref={gatilhoRef}
        type="button"
        onClick={() => (open ? fechar() : setOpen(true))}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? panelId : undefined}
        className={cn(
          'flex h-10 items-center gap-2 rounded-sm px-3 text-[13px] transition-colors',
          isField
            ? 'w-full bg-muted text-foreground hover:bg-accent'
            : count > 0
              ? 'bg-foreground font-medium text-white hover:bg-foreground-hover'
              : 'bg-muted text-foreground hover:bg-accent',
        )}
      >
        <span className={cn(isField && 'flex-1 text-left')}>{label}</span>

        {count > 0 && !isField ? (
          <span className="tabular rounded-xs bg-white/20 px-1.5 text-[11px] font-medium">
            {count}
          </span>
        ) : null}

        {count > 0 && isField ? (
          <span className="tabular text-[12px] text-muted-foreground">{count}</span>
        ) : null}

        {count === 0 || isField ? (
          <ChevronDown
            size={15}
            strokeWidth={1.5}
            className="shrink-0 text-muted-foreground"
            aria-hidden
          />
        ) : null}
      </button>

      {count > 0 && !isField ? (
        <button
          type="button"
          onClick={() => onChange([])}
          aria-label={`Limpar filtro de ${label.toLowerCase()}`}
          className="absolute top-1/2 -right-1 grid size-5 -translate-y-1/2 translate-x-full place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X size={13} strokeWidth={2} aria-hidden />
        </button>
      ) : null}

      {open ? (
        <Panel
          id={panelId}
          label={label}
          searchable={searchable}
          term={term}
          onTerm={setTerm}
          anchorRef={gatilhoRef}
          panelRef={panelRef}
        >
          {visible.length === 0 ? (
            <NadaEncontrado />
          ) : (
            visible.map((option) => (
              <Opcao
                key={option.value}
                option={option}
                marca="caixa"
                active={selected.includes(option.value)}
                onSelect={() => toggle(option.value)}
              />
            ))
          )}
        </Panel>
      ) : null}
    </div>
  );
}

/**
 * Escolha única em menu suspenso, com busca opcional.
 *
 * Mesma aparência da seleção múltipla, e o mesmo motivo para não ser um
 * `<select>` nativo: com busca e duas linhas por opção, o controle do navegador
 * não dá conta.
 */
export function SingleSelect({
  label,
  options,
  value,
  onChange,
  searchable = false,
  placeholder,
  className,
}: {
  /** Rótulo acessível; aparece antes do valor quando `showLabel`. */
  label: string;
  options: readonly MultiSelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  searchable?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const panelId = useId();
  const gatilhoRef = useRef<HTMLButtonElement>(null);

  const fechar = useCallback(() => {
    setOpen(false);
    setTerm('');
  }, []);

  const { containerRef, panelRef } = useDismiss(open, fechar);

  const current = options.find((option) => option.value === value);
  const visible = options.filter((option) => matches(option, term));

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={gatilhoRef}
        type="button"
        onClick={() => (open ? fechar() : setOpen(true))}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        className="flex h-10 w-full items-center gap-2 rounded-sm bg-muted px-3 text-[13px] text-foreground transition-colors hover:bg-accent"
      >
        <span
          className={cn('min-w-0 flex-1 truncate text-left', !current && 'text-muted-foreground')}
        >
          {current?.label ?? placeholder ?? label}
        </span>
        <ChevronDown
          size={15}
          strokeWidth={1.5}
          className="shrink-0 text-muted-foreground"
          aria-hidden
        />
      </button>

      {open ? (
        <Panel
          id={panelId}
          label={label}
          searchable={searchable}
          term={term}
          onTerm={setTerm}
          anchorRef={gatilhoRef}
          panelRef={panelRef}
        >
          {visible.length === 0 ? (
            <NadaEncontrado />
          ) : (
            visible.map((option) => (
              <Opcao
                key={option.value}
                option={option}
                marca="risco"
                active={option.value === value}
                onSelect={() => {
                  onChange(option.value);
                  fechar();
                }}
              />
            ))
          )}
        </Panel>
      ) : null}
    </div>
  );
}
